import React, { createContext, useCallback, useEffect, useState } from 'react';
import apiClient, { AUTH_EXPIRED_EVENT, buildAuthHeaders } from 'services/apiClient';

export let AuthContext = createContext();

const CLIENT_USERNAME_STORAGE_KEY = 'clientUsername';
const AUTH_COOKIE_NAME = 'token';

export default function AuthContextProvider(props) {
    const [isAuth, setAuth] = useState(false);
    const [authReady, setAuthReady] = useState(false);
    const [token, setToken] = useState(null);
    const [clientUsername, setClientUsernameState] = useState(() => {
        return localStorage.getItem(CLIENT_USERNAME_STORAGE_KEY) || '';
    });

    const setClientUsername = useCallback((username) => {
        const normalizedUsername = String(username || '').trim();
        setClientUsernameState(normalizedUsername);
        if (normalizedUsername) {
            localStorage.setItem(CLIENT_USERNAME_STORAGE_KEY, normalizedUsername);
        } else {
            localStorage.removeItem(CLIENT_USERNAME_STORAGE_KEY);
        }
    }, []);

    const readCookie = useCallback(() => {
        const cookies = document.cookie.split('; ');
        for (const cookie of cookies) {
            const separatorIndex = cookie.indexOf('=');
            const name = separatorIndex >= 0 ? cookie.slice(0, separatorIndex) : cookie;
            const value = separatorIndex >= 0 ? cookie.slice(separatorIndex + 1) : '';
            if (name === AUTH_COOKIE_NAME) {
                return value ? decodeURIComponent(value) : null;
            }
        }
        return null;
    }, []);

    const clearAuthState = useCallback(() => {
        setAuth(false);
        setToken(null);
        setClientUsername('');
    }, [setClientUsername]);

    const writeAuthCookie = useCallback((authToken) => {
        const expirationDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
        const cookieParts = [
            `${AUTH_COOKIE_NAME}=${encodeURIComponent(authToken)}`,
            `expires=${expirationDate.toUTCString()}`,
            'path=/',
            'SameSite=Strict',
        ];

        if (window.location.protocol === 'https:') {
            cookieParts.push('Secure');
        }

        document.cookie = cookieParts.join('; ');
    }, []);

    const expireAuthCookie = useCallback(() => {
        const cookieParts = [
            `${AUTH_COOKIE_NAME}=`,
            'expires=Thu, 01 Jan 1970 00:00:00 UTC',
            'path=/',
            'SameSite=Strict',
        ];

        if (window.location.protocol === 'https:') {
            cookieParts.push('Secure');
        }

        document.cookie = cookieParts.join('; ');
    }, []);

    const loadCurrentUser = useCallback(async (authToken) => {
        if (!authToken) return null;

        const response = await apiClient.get('auth/users/me/', {
            headers: buildAuthHeaders(authToken),
        });
        const username = response.data?.username || '';
        if (username) {
            setClientUsername(username);
        }
        return response.data;
    }, [setClientUsername]);

    const fetchCurrentUser = useCallback(async (authToken = token || readCookie()) => {
        return loadCurrentUser(authToken);
    }, [loadCurrentUser, readCookie, token]);

    const ensureClientUsername = useCallback(async () => {
        if (clientUsername) return clientUsername;

        const currentUser = await fetchCurrentUser();
        const recoveredUsername = currentUser?.username || '';
        if (!recoveredUsername) {
            throw new Error('Unable to identify the logged-in client.');
        }
        return recoveredUsername;
    }, [clientUsername, fetchCurrentUser]);

    const createCookie = useCallback((authToken, username = '') => {
        writeAuthCookie(authToken);
        setAuth(true);
        setToken(authToken);
        if (username) {
            setClientUsername(username);
        }
    }, [setClientUsername, writeAuthCookie]);

    const updateCookie = useCallback((authToken) => {
        writeAuthCookie(authToken);
        setAuth(true);
        setToken(authToken);
    }, [writeAuthCookie]);

    const deleteCookie = useCallback(() => {
        expireAuthCookie();
        clearAuthState();
    }, [clearAuthState, expireAuthCookie]);

    useEffect(() => {
        let isMounted = true;
        const existingToken = readCookie();

        if (!existingToken) {
            clearAuthState();
            setAuthReady(true);
            return () => {
                isMounted = false;
            };
        }

        setToken(existingToken);
        loadCurrentUser(existingToken)
            .then(() => {
                if (isMounted) setAuth(true);
            })
            .catch(() => {
                if (isMounted) deleteCookie();
            })
            .finally(() => {
                if (isMounted) setAuthReady(true);
            });

        return () => {
            isMounted = false;
        };
    }, [clearAuthState, deleteCookie, loadCurrentUser, readCookie]);

    useEffect(() => {
        window.addEventListener(AUTH_EXPIRED_EVENT, deleteCookie);
        return () => {
            window.removeEventListener(AUTH_EXPIRED_EVENT, deleteCookie);
        };
    }, [deleteCookie]);

    return (
        <AuthContext.Provider value={{
            authReady,
            isAuth,
            token,
            clientUsername,
            createCookie,
            readCookie,
            updateCookie,
            setAuth,
            deleteCookie,
            setClientUsername,
            fetchCurrentUser,
            ensureClientUsername
        }}>
            {props.children}
        </AuthContext.Provider>
    );
}
